import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTerms } from '../context/TerminologyContext';
import { getUser as getStoredUser } from '../utils/tenantStorage';
import useFeedback from '../hooks/useFeedback';
import { useModules } from '../context/ModulesContext';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  TextField,
  MenuItem,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
} from '@mui/material';
import { DashboardSkeleton } from './SkeletonLoaders';
import MetricTile from './MetricTile';
import StatusChip from './StatusChip';
import {
  AccessTime as ClockIcon,
  Today as TodayIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import {
  getTimeEntries,
  createTimeEntry,
  getEmployees,
  getCompanies,
  getAffiliates,
  getDashboardData,
  getExpenses,
  getShiftSettings,
} from '../api';

import DashboardCharts from './DashboardCharts';
import SetupBanner from './SetupBanner';
import AdminHome from './AdminHome';
import DashboardSettingsDialog from './dialogs/DashboardSettingsDialog';
import ClockCard from './ClockCard';
import { formatTimeDisplay } from '../utils/formatTime';

// Helper to format date for display
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '';
  try {
    const datePart = dateStr.split('T')[0];
    const [year, month, day] = datePart.split('-');
    return `${month}/${day}/${year}`;
  } catch (e) {
    return '';
  }
};

// Helper to format today's date as YYYY-MM-DD
const getTodayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Get start of week (Sunday)
const getWeekStart = () => {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day;
  return new Date(d.getFullYear(), d.getMonth(), diff);
};

// Get end of week (Saturday)
const getWeekEnd = () => {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() + (6 - day);
  return new Date(d.getFullYear(), d.getMonth(), diff);
};

// Format date as "MMM d"
const formatShortDate = (date) => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
};

// Get day of week (0 = Sunday)
const getDayOfWeek = () => new Date().getDay();

// Default dashboard settings
const defaultSettings = {
  payPeriod: 'weekly', // weekly, biweekly, semimonthly
  showNoEntryAlert: true,
  showUnpaidBalance: true,
  showUnpaidExpensesAlert: true,
  showLowHoursAlert: false,
  showCharts: true, // Analytics charts
};

// Load settings from localStorage
const loadSettings = () => {
  try {
    const saved = localStorage.getItem('dashboardSettings');
    return saved ? { ...defaultSettings, ...JSON.parse(saved) } : defaultSettings;
  } catch (e) {
    return defaultSettings;
  }
};

// Role switch on the home route: anyone with a managerial role lands on the
// permission-composed action-inbox (AdminHome — sections gated per permission,
// metrics team-scoped for managers). Employees keep the clock-first dashboard.
export default function Dashboard() {
  const { isAdmin, isProgramAdmin, isPayrollAdmin, isManager } = useAuth();
  if (isAdmin || isProgramAdmin || isPayrollAdmin || isManager) return <AdminHome />;
  return <EmployeeDashboard />;
}

function EmployeeDashboard() {
  const { user: currentUser, isAdmin, isManager, isProgramAdmin } = useAuth();
  const { t } = useTerms();
  // Tenant-wide employee home screen (Company Setup → General → Modules →
  // Employee home screen). 'clock' strips this page down to the Quick Time
  // Entry card. Wait for the setting before the first fetch so the page never
  // flashes the full dashboard and then collapses.
  const { employeeHome, loaded: modulesLoaded } = useModules();
  const clockOnly = employeeHome === 'clock';
  // Clock only: the manual-entry form is folded behind "Missed a punch? Add time"
  const [showMissedPunch, setShowMissedPunch] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [todayEntries, setTodayEntries] = useState([]);
  const [unpaidExpenses, setUnpaidExpenses] = useState({ count: 0, total: 0 });
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [error, setError] = useState('');
  const [success, showSuccess, clearSuccess] = useFeedback(3000);

  const [timeFormat, setTimeFormat] = useState('12h');

  // Quick entry form state
  const [quickEntry, setQuickEntry] = useState({
    employee_id: '',
    company_id: '',
    affiliate_id: '',
    start_time: '09:00',
    end_time: '17:00',
    break_minutes: 0,
    notes: '',
  });

  // Mirrors ClockCard's shift state, only to hide manual entry while clocked in.
  // null until ClockCard has heard from the server.
  const [clockedIn, setClockedIn] = useState(null);

  // Settings state
  const [settings, setSettings] = useState(loadSettings);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tempSettings, setTempSettings] = useState(settings);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      // Summary tiles, Today's Entries and Recent Activity aren't shown in
      // Clock only mode — skip their requests.
      if (!clockOnly) {
        // Load dashboard summary data
        const dashRes = await getDashboardData();
        setDashboardData(dashRes.data || {});

        // Load today's entries
        const today = getTodayStr();
        const entriesRes = await getTimeEntries({ start_date: today, end_date: today });
        setTodayEntries(entriesRes.data || []);

        // Load unpaid/pending expenses (submitted or approved but not yet paid)
        try {
          const expensesRes = await getExpenses();
          // Filter for expenses that are submitted or approved but not paid
          const pending = (expensesRes.data || []).filter(e => 
            e.status === 'submitted' || e.status === 'approved' || e.status === 'pending'
          );
          const total = pending.reduce((sum, e) => sum + parseFloat(e.total_amount || 0), 0);
          setUnpaidExpenses({ count: pending.length, total });
        } catch (expErr) {
          console.error('Error loading expenses:', expErr);
          setUnpaidExpenses({ count: 0, total: 0 });
        }
      }

      // Load reference data for quick entry and shift settings
      const [empRes, compRes, affRes, shiftSettingsRes] = await Promise.all([
        getEmployees(),
        getCompanies(),
        getAffiliates(),
        getShiftSettings().catch(() => ({ data: { time_format: '12h' } })),
      ]);
      if (shiftSettingsRes.data?.time_format) {
        setTimeFormat(shiftSettingsRes.data.time_format);
      }
      setEmployees((empRes.data || []).filter(e => e.is_active !== false));
      setCompanies(compRes.data || []);
      setAffiliates(affRes.data || []);

      // Set default employee if user has one assigned
      const user = getStoredUser() || {};
      if (user.employeeId) {
        setQuickEntry(prev => ({ ...prev, employee_id: user.employeeId }));
      } else if (empRes.data && empRes.data.length === 1) {
        setQuickEntry(prev => ({ ...prev, employee_id: empRes.data[0].id }));
      }

    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [clockOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!modulesLoaded) return;
    loadDashboardData();
  }, [loadDashboardData, modulesLoaded]);

  // Settings handlers
  const handleOpenSettings = () => {
    setTempSettings(settings);
    setSettingsOpen(true);
  };

  const handleSaveSettings = () => {
    setSettings(tempSettings);
    localStorage.setItem('dashboardSettings', JSON.stringify(tempSettings));
    setSettingsOpen(false);
  };

  const handleQuickSubmit = async (entryData = quickEntry) => {
    if (!entryData.employee_id || !entryData.company_id) {
      setError(`Please select an employee and ${t('client.singular', { lower: true })}`);
      return;
    }

    try {
      const employee = employees.find(e => e.id === entryData.employee_id);
      await createTimeEntry({
        ...entryData,
        entry_date: getTodayStr(),
        hourly_rate: employee?.hourly_rate || 0,
      });
      
      showSuccess('Time entry added successfully!');
      
      // Reload dashboard
      loadDashboardData();
      
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create entry');
    }
  };

  if (loading || !modulesLoaded) {
    return <DashboardSkeleton cardCount={4} tableRows={3} />;
  }

  // Check if user account is linked to an employee record
  const hasEmployeeLink = !!currentUser?.employeeId;

  if (!hasEmployeeLink && !isProgramAdmin && !isAdmin) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Account Not Linked to Employee Record
          </Typography>
          <Typography variant="body2">
            Your user account is not linked to an employee record. This means you cannot log time, clock in/out, or view your dashboard data.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Please contact your administrator to link your account to an employee record.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Safely get dashboard values with defaults
  const todayHours = Number(dashboardData?.todayHours) || 0;
  const weekHours = Number(dashboardData?.weekHours) || 0;
  const weekEarnings = Number(dashboardData?.weekEarnings) || 0;
  const monthHours = Number(dashboardData?.monthHours) || 0;
  const monthEarnings = Number(dashboardData?.monthEarnings) || 0;
  const unpaidHours = Number(dashboardData?.unpaidHours) || 0;
  const unpaidAmount = Number(dashboardData?.unpaidAmount) || 0;
  const totalEntries = Number(dashboardData?.totalEntries) || 0;
  const paidEntries = Number(dashboardData?.paidEntries) || 0;
  const rawAlerts = Array.isArray(dashboardData?.alerts) ? dashboardData.alerts : [];
  const recentEntries = Array.isArray(dashboardData?.recentEntries) ? dashboardData.recentEntries : [];

  // Filter alerts based on user settings and add unpaid expenses alert
  const alerts = rawAlerts.filter(alert => {
    if (alert.message === 'No time logged today' && !settings.showNoEntryAlert) return false;
    if (alert.message === 'High unpaid balance') return false; // Removed - user doesn't want this alert
    if (alert.message === 'Low weekly hours' && !settings.showLowHoursAlert) return false;
    return true;
  });

  // Add unpaid expenses alert if enabled and there are unpaid expenses
  if (settings.showUnpaidExpensesAlert && unpaidExpenses.count > 0) {
    alerts.push({
      severity: 'info',
      message: 'Pending reimbursements',
      details: `${unpaidExpenses.count} expense${unpaidExpenses.count > 1 ? 's' : ''} ($${unpaidExpenses.total.toFixed(2)}) awaiting payment`
    });
  }

  // Calculate pay period based on settings
  const getPayPeriodDates = () => {
    const today = new Date();
    const dayOfMonth = today.getDate();
    
    if (settings.payPeriod === 'biweekly') {
      // Bi-weekly: 14 day periods
      const startOfYear = new Date(today.getFullYear(), 0, 1);
      const daysSinceStart = Math.floor((today - startOfYear) / (1000 * 60 * 60 * 24));
      const periodNum = Math.floor(daysSinceStart / 14);
      const periodStart = new Date(startOfYear.getTime() + periodNum * 14 * 24 * 60 * 60 * 1000);
      const periodEnd = new Date(periodStart.getTime() + 13 * 24 * 60 * 60 * 1000);
      const daysInPeriod = 14;
      const dayInPeriod = Math.floor((today - periodStart) / (1000 * 60 * 60 * 24)) + 1;
      return { start: periodStart, end: periodEnd, dayInPeriod, daysInPeriod };
    } else if (settings.payPeriod === 'semimonthly') {
      // Semi-monthly: 1st-15th, 16th-end
      if (dayOfMonth <= 15) {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        const end = new Date(today.getFullYear(), today.getMonth(), 15);
        return { start, end, dayInPeriod: dayOfMonth, daysInPeriod: 15 };
      } else {
        const start = new Date(today.getFullYear(), today.getMonth(), 16);
        const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        const daysInPeriod = end.getDate() - 15;
        return { start, end, dayInPeriod: dayOfMonth - 15, daysInPeriod };
      }
    } else {
      // Weekly (default)
      const start = getWeekStart();
      const end = getWeekEnd();
      const dayOfWeek = getDayOfWeek();
      return { start, end, dayInPeriod: dayOfWeek + 1, daysInPeriod: 7 };
    }
  };

  const payPeriodInfo = getPayPeriodDates();
  const payPeriodProgress = (payPeriodInfo.dayInPeriod / payPeriodInfo.daysInPeriod) * 100;

  const payPeriodLabels = {
    weekly: 'Weekly',
    biweekly: 'Bi-Weekly',
    semimonthly: 'Semi-Monthly'
  };

  // Manual entry for time already worked (shared by both home-screen modes).
  const manualEntryForm = (
    <>
      <Grid container spacing={2}>
        {(isAdmin || isManager) && (
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Employee"
              value={quickEntry.employee_id}
              onChange={(e) => setQuickEntry(prev => ({ ...prev, employee_id: e.target.value }))}
              size="small"
            >
              {employees.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {emp.first_name} {emp.last_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
        <Grid item xs={12} sm={(isAdmin || isManager) ? 6 : 12}>
          <TextField
            select
            fullWidth
            label={t('client.singular')}
            value={quickEntry.company_id}
            onChange={(e) => setQuickEntry(prev => ({ ...prev, company_id: e.target.value }))}
            size="small"
            required
          >
            {companies.map((comp) => (
              <MenuItem key={comp.id} value={comp.id}>
                {comp.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label={`${t('affiliate.singular')} (Optional)`}
            value={quickEntry.affiliate_id}
            onChange={(e) => setQuickEntry(prev => ({ ...prev, affiliate_id: e.target.value }))}
            size="small"
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">None</MenuItem>
            {affiliates
              // Affiliates are always client-scoped (migration 084).
              // When a client is picked, narrow to that client's
              // affiliates. Otherwise show all so picking the affiliate
              // first still works.
              .filter(aff =>
                !quickEntry.company_id ||
                String(aff.company_id) === String(quickEntry.company_id)
              )
              .map((aff) => (
                <MenuItem key={aff.id} value={aff.id}>
                  {aff.name}
                </MenuItem>
              ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Notes (Optional)"
            value={quickEntry.notes}
            onChange={(e) => setQuickEntry(prev => ({ ...prev, notes: e.target.value }))}
            size="small"
            placeholder="Work description..."
          />
        </Grid>
      </Grid>
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              label="Start"
              type="time"
              value={quickEntry.start_time}
              onChange={(e) => setQuickEntry(prev => ({ ...prev, start_time: e.target.value }))}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              label="End"
              type="time"
              value={quickEntry.end_time}
              onChange={(e) => setQuickEntry(prev => ({ ...prev, end_time: e.target.value }))}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => handleQuickSubmit()}
              disabled={!quickEntry.employee_id || !quickEntry.company_id}
              sx={{ height: '40px' }}
            >
              + Add Manual Entry
            </Button>
          </Grid>
        </Grid>
      </Box>
    </>
  );

  // The Quick Time Entry card — the only thing on the page in Clock only mode.
  const quickEntryCard = (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <ClockIcon sx={{ mr: 1 }} />
        Quick Time Entry
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {/* The live clock. ClockCard owns punches, breaks and property
          switching for every role, so employees get the same behaviour
          managers do. */}
      <Box sx={{ mb: 3 }}>
        <ClockCard prominent onStatusChange={setClockedIn} />
      </Box>

      {/* Time already worked. Hidden while on the clock so a typed entry
          can't overlap the live shift; notes about the current shift go
          in at Clock Out. In Clock only mode it also stays folded until
          the employee asks for it. */}
      {clockedIn === false && (clockOnly ? (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              size="small"
              onClick={() => setShowMissedPunch((v) => !v)}
              startIcon={showMissedPunch ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              aria-expanded={showMissedPunch}
            >
              Missed a punch? Add time
            </Button>
            <Tooltip title="Add hours you already worked but didn't clock. Only shown while you are clocked out.">
              <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', ml: 0.5 }} />
            </Tooltip>
          </Box>
          <Collapse in={showMissedPunch}>
            <Box sx={{ mt: 1 }}>{manualEntryForm}</Box>
          </Collapse>
        </>
      ) : (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Manual Entry (for past times)
          </Typography>
          {manualEntryForm}
        </>
      ))}
    </Paper>
  );

  // Clock only: just the clock, centered. Metric tiles, Alerts & Status,
  // Today's Entries, Recent Activity and the charts are all skipped.
  if (clockOnly) {
    return (
      <Box>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={clearSuccess}>{success}</Alert>}
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} md={8}>
            {quickEntryCard}
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={clearSuccess}>{success}</Alert>}

      {(isAdmin || isProgramAdmin) && <SetupBanner />}

      <Grid container spacing={3}>
        {/* Today's Summary Tiles */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricTile
            label="Today's Hours"
            value={todayHours.toFixed(1)}
            foot={`${todayEntries.length} ${todayEntries.length === 1 ? 'entry' : 'entries'}`}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricTile
            label="This Week"
            value={weekHours.toFixed(1)}
            foot={`$${weekEarnings.toFixed(2)} earned`}
          />
        </Grid>

        {settings.showUnpaidBalance && (
          <Grid item xs={12} sm={6} md={3}>
            <MetricTile
              label="Unpaid Hours"
              value={unpaidHours.toFixed(1)}
              foot={`$${unpaidAmount.toFixed(2)} pending`}
              info="Hours worked that have not been marked paid yet"
            />
          </Grid>
        )}

        <Grid item xs={12} sm={6} md={settings.showUnpaidBalance ? 3 : 6}>
          <MetricTile
            label="This Month"
            value={monthHours.toFixed(1)}
            foot={`$${monthEarnings.toFixed(2)} total`}
          />
        </Grid>

        {/* Quick Entry Widget */}
        <Grid item xs={12} md={6}>
          {quickEntryCard}
        </Grid>

        {/* Alerts & Status */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <WarningIcon sx={{ mr: 1, color: 'warning.main' }} />
                Alerts & Status
              </Box>
              <IconButton size="small" onClick={handleOpenSettings} aria-label="Dashboard settings">
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {/* Alerts */}
            <Box sx={{ mb: 3 }}>
              {alerts.length > 0 ? (
                <List dense>
                  {alerts.map((alert, idx) => (
                    <ListItem key={idx}>
                      <ListItemIcon>
                        <WarningIcon color={alert.severity === 'high' ? 'error' : 'warning'} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={alert.message}
                        secondary={alert.details}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Alert severity="success" icon={<CheckCircleIcon />}>
                  All caught up! No pending alerts.
                </Alert>
              )}
            </Box>

            {/* Pay Period Progress */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Pay Period Progress ({payPeriodLabels[settings.payPeriod]})
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  {formatShortDate(payPeriodInfo.start)}
                </Typography>
                <Box sx={{ flexGrow: 1, mx: 1 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min(payPeriodProgress, 100)} 
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {formatShortDate(payPeriodInfo.end)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" align="center">
                Day {payPeriodInfo.dayInPeriod} of {payPeriodInfo.daysInPeriod}
              </Typography>
            </Box>

            {/* Quick Stats */}
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="h5" color="primary" fontWeight="bold">
                    {totalEntries}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total Entries
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="h5" color="success.main" fontWeight="bold">
                    {paidEntries}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Paid Entries
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Today's Entries */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <TodayIcon sx={{ mr: 1 }} />
              Today's Entries
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {todayEntries.length > 0 ? (
              <List>
                {todayEntries.map((entry, idx) => (
                  <React.Fragment key={entry.id}>
                    {idx > 0 && <Divider />}
                    <ListItem>
                      <ListItemIcon>
                        <ClockIcon color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2">
                            {entry.first_name} {entry.last_name} - {entry.company_name}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {formatTimeDisplay(entry.start_time, timeFormat)} - {formatTimeDisplay(entry.end_time, timeFormat)} • {Number(entry.hours_worked || 0).toFixed(1)} hrs
                          </Typography>
                        }
                      />
                      <Chip 
                        size="small" 
                        label={`$${Number(entry.total_pay || 0).toFixed(2)}`}
                        color="primary"
                        variant="outlined"
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <ClockIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
                <Typography color="text.secondary">
                  No entries for today yet
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <HistoryIcon sx={{ mr: 1 }} />
              Recent Activity
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {recentEntries.length > 0 ? (
              <List>
                {recentEntries.slice(0, 5).map((entry, idx) => (
                  <React.Fragment key={entry.id}>
                    {idx > 0 && <Divider />}
                    <ListItem>
                      <ListItemIcon>
                        <PersonIcon color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2">
                            {entry.first_name} {entry.last_name} - {entry.company_name}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {formatDateDisplay(entry.entry_date)} • {Number(entry.hours_worked || 0).toFixed(1)} hrs
                          </Typography>
                        }
                      />
                      <StatusChip status={entry.is_paid ? 'paid' : 'unpaid'} />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <HistoryIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
                <Typography color="text.secondary">
                  No recent activity
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Analytics Charts */}
        {settings.showCharts && (
          <Grid item xs={12}>
            <DashboardCharts />
          </Grid>
        )}
      </Grid>

      <DashboardSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSave={handleSaveSettings}
        tempSettings={tempSettings}
        setTempSettings={setTempSettings}
      />

    </Box>
  );
}
